// @ts-check
import { h, uid } from "./dom.js";
import { icon } from "./icons.js";

/**
 * Menu (DS-13) on the native popover API. Arrow keys move between items, Escape
 * closes and returns focus to the trigger.
 *
 * @param {{ trigger: HTMLElement, label: string, items: ({ text: string, icon?: string, destructive?: boolean, disabled?: boolean, onSelect: () => void } | "separator")[], placement?: "start" | "end" }} options
 */
export function menu({ trigger, label, items, placement = "end" }) {
  const popup = h("div", {
    class: "ui-popover ui-menu",
    id: uid("menu"),
    popover: "auto",
    role: "menu",
    aria: { label },
    dataset: { placement },
  });
  trigger.setAttribute("aria-haspopup", "menu");
  trigger.setAttribute("aria-expanded", "false");
  trigger.setAttribute("aria-controls", popup.id);
  /** @type {HTMLButtonElement[]} */
  const buttons = [];
  for (const item of items) {
    if (item === "separator") {
      popup.append(
        h("div", { class: "ui-menu__separator", role: "separator" }),
      );
      continue;
    }
    const entry = h(
      "button",
      {
        type: "button",
        role: "menuitem",
        class: "ui-menu-item",
        tabIndex: -1,
        disabled: item.disabled,
        dataset: { tone: item.destructive ? "destructive" : undefined },
        onClick: () => {
          popup.hidePopover();
          item.onSelect();
        },
      },
      item.icon && icon(item.icon),
      h("span", { text: item.text }),
    );
    buttons.push(entry);
    popup.append(entry);
  }
  trigger.addEventListener("click", () => {
    if (popup.matches(":popover-open")) popup.hidePopover();
    else open();
  });
  function open() {
    if (!popup.isConnected) document.body.append(popup);
    popup.showPopover();
    place();
    buttons.find((entry) => !entry.disabled)?.focus();
  }
  function place() {
    const box = trigger.getBoundingClientRect();
    const width = popup.offsetWidth;
    const left =
      placement === "end"
        ? Math.max(8, box.right - width)
        : Math.min(box.left, innerWidth - width - 8);
    const below = box.bottom + 4 + popup.offsetHeight <= innerHeight - 8;
    popup.style.setProperty("--menu-left", `${left}px`);
    popup.style.setProperty(
      "--menu-top",
      `${below ? box.bottom + 4 : Math.max(8, box.top - 4 - popup.offsetHeight)}px`,
    );
  }
  popup.addEventListener("toggle", (event) => {
    const opened = /** @type {ToggleEvent} */ (event).newState === "open";
    trigger.setAttribute("aria-expanded", String(opened));
    if (!opened && popup.contains(document.activeElement)) trigger.focus();
  });
  popup.addEventListener("keydown", (event) => {
    const enabled = buttons.filter((entry) => !entry.disabled);
    const index = enabled.indexOf(
      /** @type {HTMLButtonElement} */ (document.activeElement),
    );
    const next =
      event.key === "ArrowDown"
        ? enabled[(index + 1) % enabled.length]
        : event.key === "ArrowUp"
          ? enabled[(index - 1 + enabled.length) % enabled.length]
          : event.key === "Home"
            ? enabled[0]
            : event.key === "End"
              ? enabled.at(-1)
              : undefined;
    if (!next) return;
    event.preventDefault();
    next.focus();
  });
  return { popup, open, close: () => popup.hidePopover() };
}
